/**
 * Staff Scan & Go Verification Page
 *
 * Allows staff to:
 * - View pending Scan & Go orders
 * - Review order details
 * - Verify orders are accurate
 * - Flag orders with issues
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import { useNavigate } from 'react-router-dom';
import StoreHeader from '../../components/Layout/StoreHeader';

interface SessionItem {
  id: number;
  product: {
    id: number;
    name: string;
    price: string;
    sku?: string;
  };
  quantity: number;
  weight_lbs?: string;
  is_weighted: boolean;
  line_total: string;
}

interface PendingScanSession {
  session_id: string;
  member_name: string;
  member_code: string;
  item_count: number;
  total: string;
  created_at: string;
  checkout_at?: string;
  status: string;
  items?: SessionItem[];
}

const API_BASE = '/api';

const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || data.detail || `HTTP ${response.status}`);
  }

  return response.json();
};

const ScanGoVerification: React.FC = () => {
  const { isAuthenticated } = useStoreAuth();
  const navigate = useNavigate();

  const [pendingSessions, setPendingSessions] = useState<PendingScanSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<PendingScanSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [flagReason, setFlagReason] = useState('');
  const [showFlagModal, setShowFlagModal] = useState(false);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }

    loadPendingSessions();
    const interval = setInterval(loadPendingSessions, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, navigate]);

  const loadPendingSessions = useCallback(async () => {
    if (!navigator.onLine) return;
    try {
      const data = await apiCall('/admin/scan-and-go/pending/');
      setPendingSessions(data.sessions || []);
    } catch (err: any) {
      // Silently fail — polling will retry
      if (!pendingSessions.length) {
        setError(err.message || 'Failed to load pending orders');
      }
    } finally {
      setLoading(false);
    }
  }, [pendingSessions.length]);

  const loadSessionDetail = async (sessionId: string) => {
    try {
      setProcessing(true);
      const data = await apiCall(`/admin/scan-and-go/order/${sessionId}/`);
      setSelectedSession(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load order details');
    } finally {
      setProcessing(false);
    }
  };

  const handleVerify = async () => {
    if (!selectedSession) return;

    try {
      setProcessing(true);
      setError('');
      await apiCall(`/admin/scan-and-go/order/${selectedSession.session_id}/verify/`, {
        method: 'POST',
      });

      // Remove from list and clear selection
      setPendingSessions(prev => prev.filter(s => s.session_id !== selectedSession.session_id));
      setSelectedSession(null);
      playBeep(true);
    } catch (err: any) {
      setError(err.message || 'Failed to verify order');
      playBeep(false);
    } finally {
      setProcessing(false);
    }
  };

  const handleFlag = async () => {
    if (!selectedSession || !flagReason.trim()) return;

    try {
      setProcessing(true);
      setError('');
      await apiCall(`/admin/scan-and-go/order/${selectedSession.session_id}/flag/`, {
        method: 'POST',
        body: JSON.stringify({ reason: flagReason }),
      });

      // Remove from list and clear selection
      setPendingSessions(prev => prev.filter(s => s.session_id !== selectedSession.session_id));
      setSelectedSession(null);
      setShowFlagModal(false);
      setFlagReason('');
      playBeep(true);
    } catch (err: any) {
      setError(err.message || 'Failed to flag order');
      playBeep(false);
    } finally {
      setProcessing(false);
    }
  };

  const playBeep = (success: boolean) => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = success ? 800 : 300;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;

      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (e) {
      // Audio not supported
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isAuthenticated) return null;

  return (
    <div style={styles.page}>
      <StoreHeader />

      {/* Flag Modal */}
      {showFlagModal && selectedSession && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Flag Order</h2>
            <p style={styles.modalText}>
              Order for <strong>{selectedSession.member_name}</strong>
            </p>
            <label style={styles.inputLabel}>Reason for flagging:</label>
            <textarea
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              placeholder="Enter reason..."
              style={styles.textArea}
              autoFocus
            />
            <div style={styles.modalBtnRow}>
              <button
                onClick={() => {
                  setShowFlagModal(false);
                  setFlagReason('');
                }}
                style={styles.secondaryBtn}
              >
                Cancel
              </button>
              <button
                onClick={handleFlag}
                disabled={processing || !flagReason.trim()}
                style={{
                  ...styles.flagBtn,
                  opacity: processing || !flagReason.trim() ? 0.5 : 1,
                }}
              >
                {processing ? 'Flagging...' : 'Flag Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      <main style={styles.main}>
        <div style={styles.header}>
          <h1 style={styles.title}>Scan & Go Verification</h1>
          <span style={styles.pendingCount}>
            {pendingSessions.length} pending
          </span>
        </div>

        {error && (
          <div style={styles.errorBanner}>
            {error}
            <button onClick={() => setError('')} style={styles.errorClose}>X</button>
          </div>
        )}

        <div style={styles.layout}>
          {/* Left: Pending List */}
          <div style={styles.listSection}>
            <h2 style={styles.sectionTitle}>Waiting for Verification</h2>

            {loading ? (
              <div style={styles.loadingText}>Loading...</div>
            ) : pendingSessions.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.checkmark}>&#10003;</div>
                <p>No pending orders</p>
                <p style={styles.smallText}>Orders will appear here when customers check out</p>
              </div>
            ) : (
              <div style={styles.sessionList}>
                {pendingSessions.map((session) => (
                  <button
                    key={session.session_id}
                    onClick={() => loadSessionDetail(session.session_id)}
                    style={{
                      ...styles.sessionCard,
                      borderColor: selectedSession?.session_id === session.session_id ? '#1a4d1a' : '#e0e0e0',
                      backgroundColor: selectedSession?.session_id === session.session_id ? '#f0f7f0' : '#fff',
                    }}
                  >
                    <div style={styles.sessionHeader}>
                      <span style={styles.memberName}>{session.member_name}</span>
                      <span style={styles.sessionTime}>{formatTime(session.checkout_at || session.created_at)}</span>
                    </div>
                    <div style={styles.sessionDetails}>
                      <span>{session.item_count} items</span>
                      <span style={styles.sessionTotal}>${parseFloat(session.total).toFixed(2)}</span>
                    </div>
                    <span style={styles.memberCode}>{session.member_code}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Order Detail */}
          <div style={styles.detailSection}>
            {!selectedSession ? (
              <div style={styles.noSelection}>
                <p>Select an order to verify</p>
              </div>
            ) : processing && !selectedSession.items ? (
              <div style={styles.loadingText}>Loading order details...</div>
            ) : (
              <>
                <div style={styles.orderHeader}>
                  <div>
                    <h2 style={styles.orderMember}>{selectedSession.member_name}</h2>
                    <span style={styles.orderCode}>{selectedSession.member_code}</span>
                  </div>
                  <span style={styles.orderTotal}>${parseFloat(selectedSession.total).toFixed(2)}</span>
                </div>

                <div style={styles.itemList}>
                  <h3 style={styles.itemListTitle}>Items ({selectedSession.items?.length || selectedSession.item_count})</h3>
                  {selectedSession.items?.map((item) => (
                    <div key={item.id} style={styles.orderItem}>
                      <div style={styles.itemInfo}>
                        <span style={styles.itemName}>{item.product.name}</span>
                        <span style={styles.itemSku}>{item.product.sku || ''}</span>
                      </div>
                      <div style={styles.itemQty}>
                        {item.is_weighted
                          ? `${parseFloat(item.weight_lbs || '0').toFixed(2)} lb`
                          : `x${item.quantity}`}
                      </div>
                      <div style={styles.itemPrice}>
                        ${parseFloat(item.line_total).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                <div style={styles.actionButtons}>
                  <button
                    onClick={() => setShowFlagModal(true)}
                    style={styles.flagActionBtn}
                  >
                    FLAG ISSUE
                  </button>
                  <button
                    onClick={handleVerify}
                    disabled={processing}
                    style={{
                      ...styles.verifyBtn,
                      opacity: processing ? 0.5 : 1,
                    }}
                  >
                    {processing ? 'VERIFYING...' : 'VERIFY ORDER'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f0f0f0',
    display: 'flex',
    flexDirection: 'column',
  },
  main: {
    flex: 1,
    padding: '1rem',
    maxWidth: '1400px',
    margin: '0 auto',
    width: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    margin: 0,
  },
  pendingCount: {
    backgroundColor: '#ff9800',
    color: '#fff',
    padding: '0.5rem 1rem',
    borderRadius: '20px',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  errorBanner: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '1.125rem',
  },
  errorClose: {
    background: 'none',
    border: 'none',
    color: '#c62828',
    fontSize: '1.25rem',
    cursor: 'pointer',
    padding: '0.5rem',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
    height: 'calc(100vh - 180px)',
  },
  listSection: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1rem',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  sectionTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#666',
    margin: '0 0 1rem 0',
  },
  loadingText: {
    textAlign: 'center',
    color: '#999',
    padding: '2rem',
    fontSize: '1.125rem',
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#999',
    gap: '0.5rem',
  },
  checkmark: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#e8f5e9',
    color: '#4CAF50',
    fontSize: '2rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  smallText: {
    fontSize: '0.875rem',
    color: '#999',
  },
  sessionList: {
    flex: 1,
    overflowY: 'auto' as const,
  },
  sessionCard: {
    display: 'block',
    width: '100%',
    padding: '1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    marginBottom: '0.75rem',
    cursor: 'pointer',
    textAlign: 'left',
    backgroundColor: '#fff',
  },
  sessionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  memberName: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#333',
  },
  sessionTime: {
    fontSize: '0.875rem',
    color: '#666',
  },
  sessionDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '1rem',
    color: '#666',
    marginBottom: '0.25rem',
  },
  sessionTotal: {
    fontWeight: 'bold',
    color: '#1a4d1a',
    fontSize: '1.125rem',
  },
  memberCode: {
    fontSize: '0.75rem',
    color: '#999',
  },
  detailSection: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  noSelection: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#999',
    fontSize: '1.125rem',
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: '1rem',
    borderBottom: '2px solid #e0e0e0',
    marginBottom: '1rem',
  },
  orderMember: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  orderCode: {
    fontSize: '0.875rem',
    color: '#666',
  },
  orderTotal: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
  },
  itemList: {
    flex: 1,
    overflowY: 'auto' as const,
    marginBottom: '1rem',
  },
  itemListTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#666',
    margin: '0 0 0.75rem 0',
  },
  orderItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderBottom: '1px solid #f0f0f0',
  },
  itemInfo: {
    flex: 1,
  },
  itemName: {
    display: 'block',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#333',
  },
  itemSku: {
    display: 'block',
    fontSize: '0.75rem',
    color: '#999',
  },
  itemQty: {
    fontSize: '1rem',
    color: '#666',
    marginRight: '1rem',
    minWidth: '60px',
    textAlign: 'right',
  },
  itemPrice: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    minWidth: '70px',
    textAlign: 'right',
  },
  actionButtons: {
    display: 'flex',
    gap: '1rem',
  },
  flagActionBtn: {
    flex: 1,
    backgroundColor: '#fff',
    color: '#c62828',
    border: '2px solid #c62828',
    padding: '1rem',
    borderRadius: '12px',
    fontSize: '1.125rem',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  verifyBtn: {
    flex: 2,
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    padding: '1rem',
    borderRadius: '12px',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    cursor: 'pointer',
  },

  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '2rem',
    width: '90%',
    maxWidth: '400px',
  },
  modalTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#c62828',
    margin: '0 0 1rem 0',
  },
  modalText: {
    fontSize: '1.125rem',
    color: '#666',
    marginBottom: '1rem',
  },
  inputLabel: {
    display: 'block',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#666',
    marginBottom: '0.5rem',
  },
  textArea: {
    width: '100%',
    padding: '1rem',
    fontSize: '1rem',
    border: '2px solid #ccc',
    borderRadius: '12px',
    marginBottom: '1rem',
    resize: 'vertical' as const,
    minHeight: '100px',
  },
  modalBtnRow: {
    display: 'flex',
    gap: '1rem',
  },
  secondaryBtn: {
    flex: 1,
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    padding: '1rem',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  flagBtn: {
    flex: 1,
    backgroundColor: '#c62828',
    color: '#fff',
    border: 'none',
    padding: '1rem',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
};

export default ScanGoVerification;
